import { FC, InputHTMLAttributes, Ref } from 'react'
import style from './style.module.css'
import { csx } from '@vega-ui/utils'
import { RadioSize, RadioVariant } from './types';

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Controlled checked state of the checkbox.
   * Used in controlled components for full state management.
   */
  checked?: boolean

  /**
   * Initial checked state for uncontrolled usage.
   * Mirrors the native `defaultChecked` HTML behavior.
   */
  defaultChecked?: boolean

  /**
   * Visual variant of the checkbox, for theme or context switching.
   */
  variant?: RadioVariant

  /**
   * Size of the checkbox input and its visual marker.
   */
  size?: RadioSize

  /**
   * Disables the checkbox, making it non-interactive.
   * Also applies a visually disabled style.
   */
  disabled?: boolean

  /**
   * Ref to the native `<input type="radio">` element.
   * Useful for managing focus, setting indeterminate manually, etc.
   */
  ref?: Ref<HTMLInputElement>
}

/** A Radio is a UI component that allows users to select a single option from a group, ensuring only one item can be selected at a time within the set. */
export const Radio: FC<RadioProps> = ({
  variant = 'primary',
  size = 'md',
  defaultChecked,
  checked,
  className,
  disabled,
  ref,
  ...props
}) => {
  return (
    <input
      ref={ref}
      type='radio'
      checked={checked}
      defaultChecked={defaultChecked}
      disabled={disabled}
      className={csx(style.radio, className)}
      data-variant={variant}
      data-size={size}
      {...props}
    />
  )
}
